import React, {useState, useEffect, useCallback} from "react"
import {Switch, Button} from "antd"
import {BaseTimePicker} from "components/DateTimePicker"
import {Icon} from "components/Icon"
import {useModel} from "hooks"
import moment, {Moment} from "moment"
import {WeeklyHour} from "types/calendar"
import styles from "./WeeklyHoursEditor.module.css"
import {get} from "lodash"

const WEEKDAYS = [
    {day: 0, name: "Sunday", short: "Sun"},
    {day: 1, name: "Monday", short: "Mon"},
    {day: 2, name: "Tuesday", short: "Tue"},
    {day: 3, name: "Wednesday", short: "Wed"},
    {day: 4, name: "Thursday", short: "Thu"},
    {day: 5, name: "Friday", short: "Fri"},
    {day: 6, name: "Saturday", short: "Sat"}
]

const timeToMinutes = (timeString: string): number => {
    if (!timeString) return 0
    const [hour, minute] = timeString.split(":").map(Number)
    return hour * 60 + (minute || 0)
}

const checkRangesOverlap = (ranges: Array<{startTime: string; endTime: string}>): boolean => {
    if (ranges.length < 2) return false

    const sortedRanges = [...ranges].sort((a, b) => {
        const aStart = timeToMinutes(a.startTime)
        const bStart = timeToMinutes(b.startTime)
        return aStart - bStart
    })

    for (let i = 0; i < sortedRanges.length - 1; i++) {
        const current = sortedRanges[i]
        const next = sortedRanges[i + 1]

        const currentStart = timeToMinutes(current.startTime)
        const currentEnd = timeToMinutes(current.endTime)
        const nextStart = timeToMinutes(next.startTime)
        const nextEnd = timeToMinutes(next.endTime)

        if (currentStart >= currentEnd || nextStart >= nextEnd) {
            return true
        }

        if (currentEnd > nextStart) {
            return true
        }
    }

    return false
}

type Props = {
    weeklyHours: WeeklyHour[]
    onChange: (weeklyHours: WeeklyHour[]) => void
    error?: boolean
    onOverlapChange?: (hasOverlap: boolean) => void
}

const WeeklyHoursEditor = ({weeklyHours, onChange, error, onOverlapChange}: Props) => {
    const model = useModel()
    const userTimeFormat = model?.getUserTimeFormat?.() || "hh:mm A"

    const [localWeeklyHours, setLocalWeeklyHours] = useState<Map<number, WeeklyHour>>(new Map())

    useEffect(() => {
        const map = new Map<number, WeeklyHour>()
        weeklyHours.forEach((wh) => {
            map.set(wh.weekday, wh)
        })
        setLocalWeeklyHours(map)
    }, [weeklyHours])

    const checkAllOverlaps = useCallback((weeklyHoursMap: Map<number, WeeklyHour>): boolean => {
        for (const [, weeklyHour] of weeklyHoursMap.entries()) {
            if (weeklyHour.ranges.length >= 2) {
                if (checkRangesOverlap(weeklyHour.ranges)) {
                    return true
                }
            }
            // Also check for invalid ranges (start >= end)
            for (const range of weeklyHour.ranges) {
                const startMinutes = timeToMinutes(range.startTime)
                const endMinutes = timeToMinutes(range.endTime)
                if (startMinutes >= endMinutes) {
                    return true
                }
            }
        }
        return false
    }, [])

    useEffect(() => {
        if (onOverlapChange) {
            const hasOverlap = checkAllOverlaps(localWeeklyHours)
            onOverlapChange(hasOverlap)
        }
    }, [localWeeklyHours, onOverlapChange, checkAllOverlaps])

    const updateWeeklyHour = (weekday: number, updates: Partial<WeeklyHour>) => {
        const newMap = new Map(localWeeklyHours)
        const existing = newMap.get(weekday) || {weekday, ranges: []}
        const updated = {...existing, ...updates}
        newMap.set(weekday, updated)
        setLocalWeeklyHours(newMap)
        onChange(Array.from(newMap.values()))
    }

    const toggleDay = (weekday: number) => {
        const existing = localWeeklyHours.get(weekday)
        if (existing && existing.ranges.length > 0) {
            const newMap = new Map(localWeeklyHours)
            newMap.delete(weekday)
            setLocalWeeklyHours(newMap)
            onChange(Array.from(newMap.values()))
        } else {
            updateWeeklyHour(weekday, {
                weekday,
                ranges: [{startTime: "09:00", endTime: "17:00"}]
            })
        }
    }

    const addTimeRange = (weekday: number) => {
        const existing = localWeeklyHours.get(weekday)
        const ranges = existing?.ranges || []

        let newStartTime = "09:00"
        let newEndTime = "17:00"

        if (ranges.length > 0) {
            // Get the last range's end time and add 1 hour
            const lastRange = ranges[ranges.length - 1]
            const lastEndTime = parseTimeValue(lastRange.endTime || get(lastRange, "endTime"))
            if (lastEndTime) {
                const lastEndMinutes = timeToMinutes(lastRange.endTime || get(lastRange, "endTime"))
                // If last range ends at 23:00 or later, can't add another range
                if (lastEndMinutes >= 23 * 60) {
                    // Use default times
                    newStartTime = "09:00"
                    newEndTime = "17:00"
                } else {
                    const nextHour = moment(lastEndTime).add(1, "hour")
                    newStartTime = nextHour.format("HH:mm")
                    // Default end time to 1 hour after start, but cap at 23:59
                    const endTime = moment(nextHour).add(1, "hour")
                    if (endTime.hour() === 0 && endTime.minute() === 0) {
                        // End time would be midnight, cap at 23:59
                        newEndTime = "23:59"
                    } else {
                        newEndTime = endTime.format("HH:mm")
                    }
                }
            }
        }

        const newRanges = [...ranges, {startTime: newStartTime, endTime: newEndTime}]
        updateWeeklyHour(weekday, {ranges: newRanges})
    }

    const removeTimeRange = (weekday: number, rangeIndex: number) => {
        const existing = localWeeklyHours.get(weekday)
        if (existing) {
            const newRanges = existing.ranges.filter((_, idx) => idx !== rangeIndex)
            if (newRanges.length === 0) {
                const newMap = new Map(localWeeklyHours)
                newMap.delete(weekday)
                setLocalWeeklyHours(newMap)
                onChange(Array.from(newMap.values()))
            } else {
                updateWeeklyHour(weekday, {ranges: newRanges})
            }
        }
    }

    const updateTimeRange = (
        weekday: number,
        rangeIndex: number,
        field: "startTime" | "endTime",
        value: Moment | null
    ) => {
        const existing = localWeeklyHours.get(weekday)
        if (existing && value) {
            const newRanges = [...existing.ranges]
            newRanges[rangeIndex] = {
                ...newRanges[rangeIndex],
                [field]: value.format("HH:mm")
            }

            // Validate the updated range
            const updatedRange = newRanges[rangeIndex]
            const startMinutes = timeToMinutes(updatedRange.startTime)
            const endMinutes = timeToMinutes(updatedRange.endTime)

            // If start >= end, don't update (invalid range)
            if (startMinutes >= endMinutes) {
                return
            }

            updateWeeklyHour(weekday, {ranges: newRanges})
        }
    }

    const parseTimeValue = (timeString: string): Moment => {
        if (!timeString) {
            return null
        }
        const [hour, minute, second] = timeString.split(":")
        return moment()
            .startOf("date")
            .set("hour", +hour)
            .set("minute", +minute)
            .set("second", second ? +second : 0)
    }

    const hasOverlappingRanges = (weekday: number): boolean => {
        const existing = localWeeklyHours.get(weekday)
        if (!existing || existing.ranges.length < 2) return false
        return checkRangesOverlap(existing.ranges)
    }

    return (
        <div className={`${styles.container} ${error ? styles.error : ""}`}>
            {WEEKDAYS.map((weekday) => {
                const weeklyHour = localWeeklyHours.get(weekday.day)
                const isEnabled = !!weeklyHour && weeklyHour.ranges.length > 0

                return (
                    <div key={weekday.day} className={styles.dayRow}>
                        <div className={styles.dayHeader}>
                            <Switch checked={isEnabled} onChange={() => toggleDay(weekday.day)} size="small" />
                            <span className={styles.dayName}>{weekday.name}</span>
                        </div>

                        {isEnabled && (
                            <div className={styles.timeRanges}>
                                {weeklyHour.ranges.map((range, rangeIndex) => {
                                    const startTime = parseTimeValue(range.startTime || get(range, "startTime"))
                                    const endTime = parseTimeValue(range.endTime || get(range, "endTime"))
                                    const hasOverlap = hasOverlappingRanges(weekday.day)
                                    const startMinutes = timeToMinutes(range.startTime || get(range, "startTime"))
                                    const endMinutes = timeToMinutes(range.endTime || get(range, "endTime"))
                                    const isInvalidRange = startMinutes >= endMinutes

                                    return (
                                        <div
                                            key={rangeIndex}
                                            className={`${styles.timeRange} ${
                                                hasOverlap || isInvalidRange ? styles.timeRangeError : ""
                                            }`}>
                                            <BaseTimePicker
                                                value={startTime}
                                                onChange={(value) =>
                                                    updateTimeRange(weekday.day, rangeIndex, "startTime", value)
                                                }
                                                format={userTimeFormat}
                                                size="small"
                                                error={hasOverlap || isInvalidRange}
                                            />
                                            <span className={styles.separator}>-</span>
                                            <BaseTimePicker
                                                value={endTime}
                                                onChange={(value) =>
                                                    updateTimeRange(weekday.day, rangeIndex, "endTime", value)
                                                }
                                                format={userTimeFormat}
                                                size="small"
                                                error={hasOverlap || isInvalidRange}
                                            />
                                            {weeklyHour.ranges.length > 1 && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<Icon icon="DELETE" className={styles.deleteIcon} />}
                                                    onClick={() => removeTimeRange(weekday.day, rangeIndex)}
                                                    className={styles.removeButton}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                                {hasOverlappingRanges(weekday.day) && (
                                    <div className={styles.errorMessage}>Time ranges cannot overlap</div>
                                )}
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => addTimeRange(weekday.day)}
                                    className={styles.addButton}>
                                    + Add time
                                </Button>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default WeeklyHoursEditor
